import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, FileDown, Upload, ClipboardType, X, Maximize2, Minimize2, RotateCcw, ShieldCheck } from 'lucide-react';

const C = { navy: '#0D1B2A', deep: '#060F1A', teal: '#00B4D8', slate: 'rgba(255,255,255,0.55)', faint: 'rgba(255,255,255,0.35)', line: 'rgba(255,255,255,0.1)', green: '#2BD99F', amber: '#F5B44A', red: '#F26D6D' };
const DECISION_COLOR = { APPROVE: C.green, CONDITIONS: C.teal, REFER: C.amber, DECLINE: C.red };
const DECISION_LINE = {
  APPROVE: 'Within appetite — priced and ready to bind.',
  CONDITIONS: 'Within appetite, subject to stated conditions.',
  REFER: 'Held for a human underwriter — the model says what it is not sure of.',
  DECLINE: 'Outside appetite — with the reasons in writing.',
};
const fmt$ = v => (v || v === 0) ? `$${Math.round(v).toLocaleString()}` : '—';

function BigConfidence({ value, color }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-light font-mono" style={{ color: '#fff' }}>{pct}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full mt-2" style={{ background: 'rgba(255,255,255,0.12)' }}>
        <div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function PresentDemoPage() {
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState({});
  const [line, setLine] = useState('sme_commercial');
  const [mode, setMode] = useState('paste');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState('0.0');
  const [run, setRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [isFull, setIsFull] = useState(false);
  const timerRef = useRef(null);
  const runRef = useRef(null);
  const historyRef = useRef([]);
  runRef.current = run;
  historyRef.current = history;

  useEffect(() => {
    api.get('/insurance-classes').then(r => setClasses(r.data || {})).catch(() => {});
    api.get('/demo/runs').then(r => setHistory(r.data.runs || [])).catch(() => {});
  }, [api]);

  useEffect(() => {
    const onChange = () => setIsFull(!!document.fullscreenElement);
    const onKey = (e) => {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName);
      if (e.key === 'Escape') {
        if (document.fullscreenElement) document.exitFullscreen();
        else navigate('/demo-studio?tab=live');
      } else if ((e.key === 'f' || e.key === 'F') && !typing) {
        e.preventDefault();
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen().catch(() => {});
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !typing) {
        const h = historyRef.current;
        if (!h.length) return;
        e.preventDefault();
        const cur = runRef.current ? h.findIndex(r => r.run_id === runRef.current.run_id) : -1;
        // newest-first list: ArrowLeft = older run, ArrowRight = newer run
        const next = e.key === 'ArrowLeft' ? Math.min(cur + 1, h.length - 1) : Math.max(cur - 1, 0);
        if (next !== cur && h[next]) setRun(h[next]);
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      window.removeEventListener('keydown', onKey);
      clearInterval(timerRef.current);
    };
  }, [navigate]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => toast.error('Fullscreen blocked by the browser'));
  }, []);

  if (user?.role === 'broker') return <Navigate to="/broker" replace />;

  const execute = async () => {
    setBusy(true); setElapsed('0.0');
    const t0 = Date.now();
    timerRef.current = setInterval(() => setElapsed(((Date.now() - t0) / 1000).toFixed(1)), 100);
    try {
      let data;
      if (mode === 'paste') {
        ({ data } = await api.post('/demo/decision', { text, line }));
      } else {
        const fd = new FormData();
        fd.append('line', line); fd.append('file', file);
        ({ data } = await api.post('/demo/decision/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
      }
      setRun(data);
      setHistory(h => [data, ...h.filter(r => r.run_id !== data.run_id)].slice(0, 8));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Demo run failed'); }
    clearInterval(timerRef.current);
    setBusy(false);
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/demo/decision/${run.run_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Decision_Rationale_${run.run_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Decision Rationale PDF downloaded');
    } catch { toast.error('Could not download the rationale PDF'); }
  };

  const canRun = line && (mode === 'paste' ? text.trim().length >= 30 : !!file);
  const color = run ? (DECISION_COLOR[run.decision] || C.teal) : C.teal;
  const keyFields = run ? Object.entries(run.extracted_fields || {}).slice(0, 8) : [];

  return (
    <div className="min-h-screen flex flex-col" data-testid="present-demo-page"
      style={{ background: C.navy, backgroundImage: 'radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)', backgroundSize: '26px 26px' }}>

      {/* Top chrome */}
      <div className="flex items-center justify-between px-8 py-5">
        <div className="flex items-center gap-4">
          <span className="text-lg font-bold text-white">under<span style={{ color: C.teal }}>AI</span>ter<span style={{ color: C.slate }}>.com</span></span>
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{ background: 'rgba(0,180,216,0.12)', color: C.teal, border: '1px solid rgba(0,180,216,0.3)' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.teal }} /> Live demonstration
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleFullscreen} data-testid="present-fullscreen-btn"
            className="p-2 rounded-md transition-colors hover:bg-white/10" style={{ color: C.slate }} title={isFull ? 'Exit fullscreen' : 'Fullscreen'}>
            {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button onClick={() => navigate('/demo-studio?tab=live')} data-testid="present-exit-btn"
            className="p-2 rounded-md transition-colors hover:bg-white/10" style={{ color: C.slate }} title="Exit presentation">
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-8 pb-10 w-full max-w-5xl mx-auto">

        {/* ── Stage 1: input ── */}
        {!run && !busy && (
          <div className="w-full stagger-in" data-testid="present-input-stage">
            <p className="text-[11px] uppercase tracking-[0.3em] mb-3 text-center" style={{ color: C.faint, fontFamily: 'Georgia, serif' }}>Bring your own submission</p>
            <h1 className="text-3xl sm:text-4xl font-light text-center text-white mb-2">Your risk. Our decision. <span style={{ color: C.teal }}>Under a minute.</span></h1>
            <p className="text-sm text-center mb-8" style={{ color: C.slate }}>Nothing pre-loaded — paste the broker email or drop the document, exactly as it arrived.</p>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
              <div className="flex rounded-md overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
                <button onClick={() => setMode('paste')} data-testid="present-mode-paste"
                  className="px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-colors"
                  style={mode === 'paste' ? { background: C.teal, color: C.navy } : { color: C.slate }}>
                  <ClipboardType size={13} /> Paste text
                </button>
                <button onClick={() => setMode('upload')} data-testid="present-mode-upload"
                  className="px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-colors"
                  style={mode === 'upload' ? { background: C.teal, color: C.navy } : { color: C.slate }}>
                  <Upload size={13} /> Upload document
                </button>
              </div>
              <select value={line} onChange={e => setLine(e.target.value)} data-testid="present-line-select"
                className="text-xs rounded-md px-3 py-2" style={{ background: C.deep, border: `1px solid ${C.line}`, color: '#fff' }}>
                {Object.keys(classes).length === 0 && <option value={line}>Loading classes…</option>}
                {Object.values(classes).filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {mode === 'paste' ? (
              <textarea value={text} onChange={e => setText(e.target.value)} rows={8} data-testid="present-text-input"
                placeholder="Paste the broker email or submission text here…"
                className="w-full rounded-md p-4 text-sm font-mono outline-none resize-none"
                style={{ background: C.deep, border: `1px solid ${C.line}`, color: '#E7EDF4', caretColor: C.teal }} />
            ) : (
              <div className="w-full rounded-md p-8 text-center" style={{ background: C.deep, border: `1px dashed rgba(255,255,255,0.25)` }}>
                <input type="file" accept=".pdf,.csv,.xlsx,.txt,.eml,.msg" data-testid="present-file-input"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                  className="text-xs text-white file:mr-3 file:px-4 file:py-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#00B4D8] file:text-[#0D1B2A]" />
                <p className="text-[11px] mt-3" style={{ color: C.slate }}>{file ? file.name : 'PDF, spreadsheet or email export — read live, never stored in the live book.'}</p>
              </div>
            )}

            <div className="flex justify-center mt-6">
              <button onClick={execute} disabled={!canRun} data-testid="present-run-btn"
                className="px-8 py-3 rounded-md text-sm font-bold transition-all disabled:opacity-40"
                style={{ background: C.teal, color: C.navy }}>
                Get the decision
              </button>
            </div>
            <p className="flex items-center justify-center gap-1.5 text-[10px] mt-5" style={{ color: C.faint }}>
              <ShieldCheck size={12} /> Demo runs never enter the live book — deleted on request in one action.
            </p>
          </div>
        )}

        {/* ── Stage 2: deciding ── */}
        {busy && (
          <div className="text-center stagger-in" data-testid="present-deciding-stage">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-6" style={{ color: C.teal }} />
            <p className="text-6xl font-light font-mono text-white tabular-nums" data-testid="present-timer">{elapsed}<span className="text-2xl" style={{ color: C.slate }}>s</span></p>
            <p className="text-sm mt-4 uppercase tracking-[0.25em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Reading · scoring · pricing · writing the rationale</p>
          </div>
        )}

        {/* ── Stage 3: the decision ── */}
        {run && !busy && (
          <div className="w-full stagger-in" data-testid="present-result-stage">
            <div className="text-center mb-8">
              <p className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: C.faint, fontFamily: 'Georgia, serif' }}>
                {run.business_name || 'Submission'} · {(run.line || '').replace(/_/g, ' ')}
              </p>
              <h1 className="text-7xl sm:text-8xl font-bold tracking-tight" style={{ color }} data-testid="present-decision">{run.decision}</h1>
              <p className="text-base mt-4" style={{ color: C.slate }}>{DECISION_LINE[run.decision] || ''}</p>
              <p className="text-sm mt-2 font-mono" style={{ color: C.teal }} data-testid="present-elapsed">decided in {run.elapsed_seconds}s</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-md p-5" style={{ background: C.deep, border: `1px solid ${C.line}` }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: C.faint }}>Model confidence</p>
                <div data-testid="present-confidence"><BigConfidence value={run.confidence} color={color} /></div>
              </div>
              <div className="rounded-md p-5" style={{ background: C.deep, border: `1px solid ${C.line}` }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: C.faint }}>Indicative premium</p>
                <p className="text-4xl font-light font-mono text-white" data-testid="present-premium">{fmt$(run.premium)}</p>
                {run.total_payable ? <p className="text-[11px] mt-1" style={{ color: C.slate }}>total payable {fmt$(run.total_payable)}</p> : null}
              </div>
              <div className="rounded-md p-5" style={{ background: C.deep, border: `1px solid ${C.line}` }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: C.faint }}>Composite score</p>
                <p className="text-4xl font-light font-mono text-white">{run.composite_score?.toLocaleString()}</p>
                <p className="text-[11px] mt-1" style={{ color: C.slate }}>{run.stp ? 'straight-through eligible' : `model ${(run.model_version || '').split('/')[0]}`}</p>
              </div>
            </div>

            {run.rationale && (
              <div className="rounded-md p-6 mb-6" style={{ background: C.deep, border: `1px solid ${C.line}`, borderLeft: `3px solid ${color}` }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: C.faint, fontFamily: 'Georgia, serif' }}>The rationale, in writing</p>
                <p className="text-[15px] leading-relaxed" style={{ color: '#E7EDF4' }} data-testid="present-rationale">{run.rationale}</p>
              </div>
            )}

            {keyFields.length > 0 && (
              <div className="rounded-md p-5 mb-8" style={{ background: C.deep, border: `1px solid ${C.line}` }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: C.faint, fontFamily: 'Georgia, serif' }}>What the model read — with stated confidence</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3" data-testid="present-fields">
                  {keyFields.map(([k, v]) => (
                    <div key={k}>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.faint }}>{k.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-mono text-white truncate">{String(v.value)}</p>
                      <p className="text-[10px] font-mono" style={{ color: v.confidence < 0.6 ? C.amber : C.teal }}>{Math.round((v.confidence || 0) * 100)}% sure</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <button onClick={downloadPdf} data-testid="present-pdf-btn"
                className="px-6 py-2.5 rounded-md text-xs font-bold flex items-center gap-2"
                style={{ background: C.teal, color: C.navy }}>
                <FileDown size={14} /> Rationale PDF — something you keep
              </button>
              <button onClick={() => { setRun(null); setText(''); setFile(null); }} data-testid="present-reset-btn"
                className="px-6 py-2.5 rounded-md text-xs font-medium flex items-center gap-2 transition-colors hover:bg-white/10"
                style={{ border: `1px solid ${C.line}`, color: '#fff' }}>
                <RotateCcw size={14} /> Run another
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Pitch replay strip ── */}
      {history.length > 0 && !busy && (
        <div className="px-8 pb-3" data-testid="present-replay-strip">
          <p className="text-[9px] uppercase tracking-[0.25em] mb-2 text-center" style={{ color: C.faint, fontFamily: 'Georgia, serif' }}>Pitch replay — decided this session, no re-run needed</p>
          <div className="flex justify-center gap-2 flex-wrap">
            {history.map(h => {
              const hc = DECISION_COLOR[h.decision] || C.teal;
              const active = run?.run_id === h.run_id;
              return (
                <button key={h.run_id} onClick={() => setRun(h)} data-testid={`present-replay-chip-${h.run_id}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md text-left transition-colors"
                  style={{ background: active ? 'rgba(0,180,216,0.12)' : C.deep, border: `1px solid ${active ? 'rgba(0,180,216,0.5)' : C.line}` }}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: hc }} />
                  <span className="text-[11px] text-white max-w-[140px] truncate">{h.business_name || (h.line || '').replace(/_/g, ' ') || 'Submission'}</span>
                  <span className="text-[10px] font-bold" style={{ color: hc }}>{h.decision}</span>
                  <span className="text-[10px] font-mono" style={{ color: C.faint }}>{h.elapsed_seconds}s</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-center text-[10px] pb-4" style={{ color: C.faint }} data-testid="present-hotkey-hint">
        <span className="font-mono px-1.5 py-0.5 rounded mr-1" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}>F</span>fullscreen
        <span className="mx-2">·</span>
        <span className="font-mono px-1.5 py-0.5 rounded mr-1" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}>Esc</span>exit
        <span className="mx-2">·</span>
        <span className="font-mono px-1.5 py-0.5 rounded mr-1" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}>←→</span>replay
        <span className="mx-2">·</span>
        underAIter.com — Evolutionary Insurance Intelligence · Indicative evaluation for demonstration; not an offer of insurance.
      </p>
    </div>
  );
}
